import React, {
  forwardRef,
  useReducer,
  type ReactElement,
  type ReactNode,
} from 'react';
import { twMerge } from 'tailwind-merge';

import { ProgressiveImage as StoryWindProgressiveImage } from '@videoblocks/storywind';
import { ProgressiveImageProps as StoryWindProgressiveImageProps } from '@videoblocks/storywind/components/ProgressiveImage';

import {
  progressiveImageReducer,
  initialState,
} from './progressiveImageReducer';

interface RequiredImageProps extends StoryWindProgressiveImageProps {
  src: string;
  shouldLazyLoad?: boolean;
  fallbackComponent?: ReactNode;
  loadingComponent?: ReactNode;
  containerClassName?: string;
}

interface ProgressiveImageProps extends RequiredImageProps {
  height: Required<StoryWindProgressiveImageProps>['height'];
  width: Required<StoryWindProgressiveImageProps>['width'];
}

const ProgressiveImage = forwardRef<HTMLImageElement, ProgressiveImageProps>(
  (
    {
      id,
      className,
      src,
      alt = '',
      height,
      width,
      shouldLazyLoad = false,
      fallbackComponent,
      loadingComponent,
      containerClassName,
      onError,
      onLoad,
      ...other
    },
    ref
  ): ReactElement => {
    const [state, dispatch] = useReducer(progressiveImageReducer, initialState);
    const { imageError, imageLoading } = state;
    const loadingType = shouldLazyLoad ? 'lazy' : 'eager';

    const handleImageError = (
      event: React.SyntheticEvent<HTMLImageElement>
    ) => {
      dispatch({ type: 'SET_ERROR' });
      onError?.(event);
    };

    const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
      dispatch({ type: 'SET_LOADED' });
      onLoad?.(event);
    };
    if (imageError) {
      return (
        <div
          className={twMerge('flex bg-blue-900', containerClassName)}
          style={{
            height,
            width,
          }}
        >
          {fallbackComponent}
        </div>
      );
    }

    return (
      <div className={containerClassName}>
        {imageLoading && loadingComponent}
        <StoryWindProgressiveImage
          ref={ref}
          className={className}
          id={id}
          src={src}
          alt={alt}
          width={width}
          height={height}
          loading={loadingType}
          onError={handleImageError}
          onLoad={handleImageLoad}
          {...other}
          placeholder="empty"
        />
      </div>
    );
  }
);

ProgressiveImage.displayName = 'ProgressiveImage';

export default ProgressiveImage;
